import { useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import CheckIcon from '@admin-platform/ui/icons/outline/check.icon.svg?react';
import ChevronDownIcon from '@admin-platform/ui/icons/solid/chevron-down.icon.svg?react';
import SearchIcon from '@admin-platform/ui/icons/solid/search.icon.svg?react';

import { useEscapeLayer } from '../../hooks/useEscapeLayer';
import { rankByRelaxedSearch } from '../../utils/relaxedSearch';
import styles from './SingleSelect.module.css';

export interface SelectOption {
  label: string;
  value: number | string;
  disabled?: boolean;
}

interface SingleSelectProps {
  options: SelectOption[];
  value?: number | string;
  onChange: (value: number | string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  error?: boolean;
  required?: boolean;
  id?: string;
  /** Enable search functionality */
  searchable?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Callback when search value changes */
  onSearchChange?: (searchValue: string) => void;
  /**
   * Called when the select empties its own search field on close (a pick,
   * Escape, a click outside). `onSearchChange` is not told about that reset,
   * so a picker that searches on the server resets its query here, or the next
   * open shows the old matches under an empty field. Opt-in: a picker that
   * keeps its last results on purpose leaves it out.
   */
  onSearchClear?: () => void;
  /** Accessible name when no visible label points at the select (e.g. a table cell). */
  'aria-label'?: string;
  /**
   * Extra class for the open options panel — e.g. to let it grow wider than a
   * narrow trigger (a table cell) so long labels are readable.
   */
  dropdownClassName?: string;
  /**
   * `inline` shows the value as plain text (e.g. in a table cell) and only
   * looks like a field while open or focused from the keyboard.
   */
  variant?: 'default' | 'inline';
}

export function SingleSelect({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  loading = false,
  className,
  error = false,
  required = false,
  id,
  searchable = false,
  searchPlaceholder = 'Search...',
  onSearchChange,
  onSearchClear,
  'aria-label': ariaLabel,
  dropdownClassName,
  variant = 'default',
}: SingleSelectProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number | null;
    bottom: number | null;
    left: number;
    width: number;
    flipped: boolean;
  } | null>(null);
  const [searchValue, setSearchValue] = useState('');
  // A ref, so the outside-click listener sees the current callback without
  // re-subscribing on every render.
  const onSearchClearRef = useRef(onSearchClear);
  onSearchClearRef.current = onSearchClear;
  const clearSearch = (): void => {
    setSearchValue('');
    onSearchClearRef.current?.();
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((option) => option.value === value);
  // Loading may close the *trigger* (initial fetch), but must not disable or
  // unmount an already-open searchable menu — async onSearchChange would
  // otherwise steal focus after the first keystroke.
  const isDisabled = disabled || (loading && !isOpen);

  const filteredOptions =
    searchable && searchValue.trim()
      ? rankByRelaxedSearch(options, searchValue, (option) => option.label)
      : options;

  // Calculate dropdown position; flip upward when there isn't enough space below.
  const calculateDropdownPosition = () => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();
    // --dropdown-height-medium is 270px; add 8px buffer before flipping
    const DROPDOWN_HEIGHT = 278;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipped = spaceBelow < DROPDOWN_HEIGHT;

    if (flipped) {
      return {
        top: null,
        bottom: window.innerHeight - rect.top,
        left: rect.left,
        width: rect.width,
        flipped: true,
      };
    }

    return {
      top: rect.bottom,
      bottom: null,
      left: rect.left,
      width: rect.width,
      flipped: false,
    };
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideContainer =
        containerRef.current && !containerRef.current.contains(event.target as Node);
      const isOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setDropdownPosition(null);
        if (searchable) {
          clearSearch();
        }
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        const position = calculateDropdownPosition();
        setDropdownPosition(position);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, searchable]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (!isDisabled) {
            setIsOpen(true);
          }
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            const option = filteredOptions[highlightedIndex];
            if (!option.disabled) {
              onChange(option.value);
              setIsOpen(false);
              setHighlightedIndex(-1);
            }
          }
          break;
        case 'Tab':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, highlightedIndex, filteredOptions, onChange, isDisabled, searchable]);

  // Escape closes the list, and only the list, while it is the top overlay.
  useEscapeLayer(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (searchable) clearSearch();
  }, isOpen);

  useEffect(() => {
    if (isOpen && optionsRef.current) {
      const highlightedElement = optionsRef.current.children[highlightedIndex] as
        | HTMLElement
        | undefined;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleOptionClick = (option: SelectOption) => {
    if (!option.disabled) {
      onChange(option.value);
      setIsOpen(false);
      setHighlightedIndex(-1);
      if (searchable) {
        clearSearch();
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    if (onSearchChange) {
      onSearchChange(newValue);
    }
  };

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (!isOpen || !searchable) return;
    setHighlightedIndex(searchValue.trim() ? 0 : -1);
  }, [isOpen, searchable, searchValue]);

  const handleTriggerClick = () => {
    if (!isDisabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      setHighlightedIndex(-1);

      // Calculate position when opening
      if (newIsOpen) {
        const position = calculateDropdownPosition();
        setDropdownPosition(position);
      } else {
        setDropdownPosition(null);
      }
    }
  };

  return (
    <div ref={containerRef} className={classNames(styles.Container, className)}>
      <div
        className={classNames(styles.Trigger, {
          [styles.inline]: variant === 'inline',
          [styles.open]: isOpen,
          [styles.disabled]: isDisabled,
          [styles.error]: error,
        })}
        onClick={handleTriggerClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTriggerClick();
          }
        }}
        tabIndex={isDisabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? 'select-options' : undefined}
        aria-haspopup="listbox"
        aria-disabled={isDisabled || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : id}
        id={id}
        aria-required={required}
      >
        <span className={styles.Value}>
          {loading && !isOpen ? 'Loading...' : selectedOption ? selectedOption.label : placeholder}
        </span>
        {loading && !isOpen ? (
          <div className={styles.Spinner} />
        ) : (
          <ChevronDownIcon className={classNames(styles.Icon, { [styles.rotated]: isOpen })} />
        )}
      </div>

      {isOpen && dropdownPosition && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={dropdownRef}
              className={classNames(
                styles.OptionsContainer,
                { [styles.flipped]: dropdownPosition.flipped },
                dropdownClassName,
              )}
              style={
                {
                  '--dropdown-top':
                    dropdownPosition.top != null ? `${dropdownPosition.top}px` : 'unset',
                  '--dropdown-bottom':
                    dropdownPosition.bottom != null ? `${dropdownPosition.bottom}px` : 'unset',
                  '--dropdown-left': `${dropdownPosition.left}px`,
                  '--dropdown-width': `${dropdownPosition.width}px`,
                } as React.CSSProperties
              }
            >
              {searchable && (
                <div className={styles.SearchContainer}>
                  <div className={styles.SearchInputWrapper}>
                    <SearchIcon className={styles.SearchIcon} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      className={styles.SearchInput}
                      placeholder={searchPlaceholder}
                      value={searchValue}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      aria-label={searchPlaceholder}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>
              )}
              <div
                ref={optionsRef}
                className={styles.Options}
                role="listbox"
                id="select-options"
                aria-label={placeholder}
                aria-busy={loading || undefined}
              >
                {filteredOptions.length === 0 ? (
                  <div className={styles.NoResults}>
                    {loading ? 'Searching…' : 'No results found'}
                  </div>
                ) : (
                  filteredOptions.map((option, index) => (
                    <div
                      key={option.value}
                      className={classNames(styles.Option, {
                        [styles.highlighted]: index === highlightedIndex,
                        [styles.selected]: option.value === value,
                        [styles.disabled]: option.disabled,
                      })}
                      onClick={() => handleOptionClick(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleOptionClick(option);
                        }
                      }}
                      tabIndex={option.disabled ? -1 : 0}
                      role="option"
                      aria-selected={option.value === value}
                      aria-disabled={option.disabled}
                    >
                      <span className={styles.OptionLabel}>{option.label}</span>
                      {option.value === value && <CheckIcon className={styles.CheckIcon} />}
                    </div>
                  ))
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
